import { Image } from '@components/core/Image';
import Icon from '@components/core/Icon';
import Button from '@components/core/Button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { useRef } from 'react';
import 'swiper/css';

interface IImagesCarousel {
  images: any,
  description?: string,
  classNameName: boolean,
  sectionFullWidth: boolean,
  autoPlaySpeed: number
}

export default function ImagesCarousel(props: IImagesCarousel) {

  const swiperRef = useRef<any>(null);
  const { images, autoPlaySpeed, sectionFullWidth } = props;

  return (
    <div className={ sectionFullWidth
      ? 'w-full relative flex items-center'
      : 'relative flex items-center !h-fit px-4 md:px-6 lg:px-8 2xl:px-12 m-auto'
    }>
      {
        sectionFullWidth ?
          <Button type='icon' className='left-3 lg:left-10 absolute z-10 text-cta hover:!mix-blend-normal -translate-y-[50%]' aria-label='Carousel previous' onClick={ () => swiperRef.current.slidePrev() }>
            <Icon name={ 'ArrowBack' } className='!h-4 !w-4 md:!h-6 md:!w-6' />
          </Button>
          :
          <Button type='icon' className='left-3 lg:left-10 xl:left-32 absolute z-10 text-cta -translate-y-[50%] hover:!mix-blend-normal' aria-label='Carousel previous' onClick={ () => swiperRef.current.slidePrev() }>
            <Icon name={ 'ArrowBack' } className='!h-4 !w-4 md:!h-6 md:!w-6' />
          </Button>
      }

      <Swiper
        loop={ true }
        spaceBetween={ 20 }
        grabCursor={ true }
        slidesPerView={ 1 }
        autoplay={ {
          delay: autoPlaySpeed ? autoPlaySpeed * 1000 : 6000,
          disableOnInteraction: false,
        } }
        modules={ [ Autoplay ] }
        onInit={ (swiper) => {
          swiperRef.current = swiper;
        } }
      >
        {
          images.map((image, i) =>
            <SwiperSlide key={ i }>
              {
                <div className={ sectionFullWidth ? 'relative w-full h-[400px] md:h-[500px]' : 'relative w-full' }>
                  <div className={ sectionFullWidth ? 'relative w-full h-[calc(100%-27px)]': 'max-h-96 max-w-[840px] h-[300px] md:h-[400px] mx-auto' }>
                    <div className='relative h-full'>
                      <Image
                        sanityImage={ image }
                        objectFit={ sectionFullWidth ? 'cover' : 'contain' }
                        layout={ 'fill' }
                        className={ sectionFullWidth ? 'h-full' : '!w-auto !h-auto max-w-full max-h-full rounded-[32px] mx-auto' }
                        sizes={ sectionFullWidth ? '(min-width: 1280px) 1440px, (min-width: 768px) 750px, 640px' : '(min-width: 1280px) 840px, (min-width: 768px) 750px, 640px' }
                        priority={ i === 0 }
                      />
                      <div className='absolute bottom-0 left-0 right-0 pt-24 pb-8 px-8 md:pb-12 md:px-12 rounded-b-[32px] mx-auto max-w-[840px]'>
                        {image.quote && (
                          <div className='bg-white/90 backdrop-blur-sm rounded-xl p-5 md:p-7 shadow-xl border border-white/20 max-w-md'>
                            <div className='relative'>
                              <svg className='absolute -top-2 -left-2 w-6 h-6 text-primary opacity-60' fill='currentColor' viewBox='0 0 24 24'>
                                <path d='M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z' />
                              </svg>
                              <p className='text-body-lg leading-relaxed text-shade-900 tracking-wide italic px-8'>
                                {image.quote}
                              </p>
                              <svg className='absolute -bottom-2 -right-2 w-6 h-6 text-primary opacity-60 transform rotate-180' fill='currentColor' viewBox='0 0 24 24'>
                                <path d='M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z' />
                              </svg>
                            </div>
                            {image.sender && (
                              <div className='mt-3'>
                                <p className='text-body-sm text-shade-700 tracking-wide uppercase'>
                                  — {image.sender}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        {!image.quote && image.sender && (
                          <p className='mt-3 text-body-md md:text-body-lg text-white tracking-wide'>
                            - {image.sender}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={ sectionFullWidth ? 'px-5 max-w-[840px] mx-auto' : 'w-fit mx-auto px-5' }>
                    { image.caption &&
                      <p className='text-body-sm md:text-body-md mt-2 text-shade-700'>{ image.caption }</p>
                    }
                  </div>
                </div>
              }
            </SwiperSlide>
          )
        }
      </Swiper>
      {
        sectionFullWidth ?
          <Button type='icon' className='right-3 lg:right-10 text-cta absolute z-10 hover:!mix-blend-normal -translate-y-[50%]' aria-label='Carousel next' onClick={ () => swiperRef.current.slideNext() }>
            <Icon name={ 'ArrowForward' } className='!h-4 !w-4 md:!h-6 md:!w-6' />
          </Button> :
          <Button type='icon' className='right-3 lg:right-10 xl:right-32 absolute z-10 text-cta -translate-y-[50%] hover:!mix-blend-normal' aria-label='Carousel next' onClick={ () => swiperRef.current.slideNext() }>
            <Icon name={ 'ArrowForward' } className='!h-4 !w-4 md:!h-6 md:!w-6' />
          </Button>
      }
    </div>
  );
}
